/** @jsxImportSource @emotion/react */
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import { useThemeData } from './hooks/useThemeData';
import { useContentEditable } from './hooks/useContentEditable';
import { transformHtmlContentForPDF } from './utils/contentTransforms';

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    id?: string;
    className?: string;
    editorHtml?: string;
    classnames?: string;
    typographyClass?: string;
    transformData?: boolean;
    functionToTransform?: string;
}

const RichTextEditableTemplateView: FunctionComponent<
    React.PropsWithChildren<commonTypes.WidgetInstanceProps>
> = (props) => {
    const widgetProperties =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) || {};
    const {
        editorHtml = 'Click to edit...',
        transformData = false,
        functionToTransform,
    } = widgetProperties;
    const { data } = props;
    const i18n = commonHooks.useLocalization();
    const appContext = commonHooks.useAppContext();
    const { inEditMode, documentType } = appContext;
    const classnames = widgetProperties.classnames || '';
    const contentRef = useRef<HTMLDivElement>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [tempContent, setTempContent] = useState(editorHtml);

    // Use theme data hook
    const { getAllFonts, getColorVariables } = useThemeData();
    const allFonts = getAllFonts();
    const colorVariables = getColorVariables();

    // Update temp content when widget properties change or component remounts
    useEffect(() => {
        setTempContent(editorHtml);
    }, [editorHtml]);

    // Ensure content is preserved during drag operations
    useEffect(() => {
        // If tempContent is different from editorHtml and we're not editing,
        // it might indicate the component was remounted during drag
        if (!isEditing && tempContent !== editorHtml && editorHtml !== 'Click to edit...') {
            setTempContent(editorHtml);
        }
    }, [editorHtml, tempContent, isEditing]);

    // Use content editable hook
    useContentEditable(
        isEditing,
        tempContent,
        setTempContent,
        contentRef,
        allFonts,
        colorVariables,
        props.widgetInstanceId || '',
        setIsEditing,
        undefined, // templateResourceStrings - not available at this level
        data?.resourceStrings as Record<string, string> | undefined, // siteResourceStrings - contains all available strings
        appContext.accessToken as string,
        data
    );

    const themeHtmlBlockStyles = css`
        color: var(--default-text-color);
        font-family: var(--font-family-regular);
        color: var(--default-text-color);
        font-weight: var(--font-weight-regular);
        font-size: var(--font-size-regular);
    `;

    // Helper function to transform data placeholders in content
    const transformDataPlaceholders = (content: string): string => {
        if (!transformData || !functionToTransform || !data) {
            // If no transformation function, just replace simple placeholders
            return content.replace(/\{\{([^}]+)\}\}/g, (match, propertyPath) => {
                try {
                    // Simple property path resolution (e.g., "fund.name" -> data.fund.name)
                    const value = propertyPath
                        .split('.')
                        .reduce((obj: any, key: string) => obj?.[key], data);
                    return value !== undefined ? String(value) : match;
                } catch (error) {
                    console.log('Error resolving property path:', propertyPath, error);
                    return match;
                }
            });
        }

        try {
            // Use transformDataWithGlobals for advanced transformations
            const transformFunctionName = `${props.widgetInstanceId}-richtext-transform`;
            const transformedData = commonUtils.transformDataWithGlobals(
                transformFunctionName,
                functionToTransform,
                {
                    data,
                    content, // Pass the content so users can access it in their transformation
                    i18n, // Pass i18n utilities for formatting
                }
            );

            // If transformation returns a string, use it directly
            if (typeof transformedData === 'string') {
                return transformedData;
            }

            // Otherwise, apply the simple placeholder replacement to the original content
            return content.replace(/\{\{([^}]+)\}\}/g, (match, propertyPath) => {
                try {
                    const value = propertyPath
                        .split('.')
                        .reduce((obj: any, key: string) => obj?.[key], transformedData || data);
                    return value !== undefined ? String(value) : match;
                } catch (error) {
                    console.log('Error resolving transformed property path:', propertyPath, error);
                    return match;
                }
            });
        } catch (error) {
            console.log('Data transformation error:', error);
            // Fallback to simple replacement on error
            return content.replace(/\{\{([^}]+)\}\}/g, (match, propertyPath) => {
                try {
                    const value = propertyPath
                        .split('.')
                        .reduce((obj: any, key: string) => obj?.[key], data);
                    return value !== undefined ? String(value) : match;
                } catch (fallbackError) {
                    return match;
                }
            });
        }
    };

    const handleClick = () => {
        // Only allow editing if in edit mode and component is properly mounted
        if (!inEditMode || !contentRef.current) {
            return;
        }

        if (!isEditing) {
            setIsEditing(true);
        }
    };

    return (
        <div key={`rich-text-${props.widgetInstanceId}`}>
            <div
                ref={contentRef}
                key={`content-${props.widgetInstanceId}`}
                css={css`
                    ${themeHtmlBlockStyles}
                    ${getTextSchemaProperties(widgetProperties, data?.colorSpace)}
          ${getStyleSchemaProperties(widgetProperties)}
          ${getLayoutSchemaProperties(widgetProperties)}
          ${isEditing
                        ? `
            border: 2px dashed #007cba;
            min-height: 100px;
            padding: 15px;
            outline: none;
            background-color: #fafbfc;
          `
                        : inEditMode
                          ? `
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 15px;
            &:hover {
              background-color: rgba(0, 124, 186, 0.05);
              border: 1px dashed #007cba;
            }
          `
                          : `
            cursor: default;
            padding: 15px;
          `}
                `}
                dangerouslySetInnerHTML={
                    !isEditing
                        ? {
                              __html: (() => {
                                  let processedContent = tempContent || editorHtml;

                                  // Remove data placeholder styling when not editing
                                  processedContent = processedContent.replace(
                                      /<span[^>]*data-property-key[^>]*>(.*?)<\/span>/g,
                                      '$1'
                                  );

                                  // First apply i18n formatting
                                  processedContent =
                                      i18n.format(processedContent, data) || 'Click to edit...';

                                  // Then apply data placeholder transformations
                                  processedContent = transformDataPlaceholders(processedContent);

                                  // Finally apply PDF transformations if rendering for PDF
                                  return documentType === 'pdf'
                                      ? transformHtmlContentForPDF(processedContent)
                                      : processedContent;
                              })(),
                          }
                        : undefined
                }
                className={`${classnames} ${widgetProperties.typographyClass || ''}`}
                data-testid="rich-text-editable"
                onClick={handleClick}
                title={!isEditing && inEditMode ? 'Click to edit content' : ''}
            />
        </div>
    );
};

export default RichTextEditableTemplateView;
